import { useCallback, useState } from 'react';

import type { IAbsence } from '@src/models/interfaces/absence.interface';
import { useAbsences } from '@src/packages/absences/hooks';

type UseWorkJourneyScheduleAbsenceParams = {
  /** Called after a successful save so the visible tab can refresh. */
  onSaved?: () => void;
};

/**
 * Header action "Schedule absence" — reuses the absences form drawer instead of
 * a journey-specific one, since an absence is an organization exception.
 */
export const useWorkJourneyScheduleAbsence = ({
  onSaved,
}: UseWorkJourneyScheduleAbsenceParams = {}) => {
  const [open, setOpen] = useState(false);
  const { createAbsence, loading: saving } = useAbsences();

  const openDrawer = useCallback(() => setOpen(true), []);
  const closeDrawer = useCallback(() => setOpen(false), []);

  const handleSave = useCallback(
    async (absence: Partial<IAbsence>) => {
      const result = await createAbsence(absence);
      if (result?.success) {
        onSaved?.();
      }
    },
    [createAbsence, onSaved],
  );

  return {
    open,
    openDrawer,
    closeDrawer,
    handleSave,
    loading: saving,
  };
};
